import { type Dispatch, memo, type SetStateAction } from "react";

import clsx from "clsx";

import { type ColumnDef, type Table } from "@tanstack/react-table";

import { type ExtendedColumnDef } from "./table";
import { TableCell } from "./table-cell";

interface Props<TData> {
  columns: ColumnDef<TData>[];
  table: Table<TData>;
  tableHeadHeight: number;
  loading?: boolean;
  shouldHighlightOnHover?: boolean;
  currentColumnIndexHovered?: number | null;
  setCurrentColumnIndexHovered?: Dispatch<SetStateAction<number | null>>;
}

export const TableBody = <TData,>(props: Props<TData>) => {
  const { columns, table, tableHeadHeight, loading, shouldHighlightOnHover } =
    props;

  return (
    <tbody>
      {!loading && (
        <>
          {table.getTopRows().map((row) => (
            <tr
              key={row.id}
              className={clsx("bg-v2-action-secondary h-[1px]", {
                "hover:bg-v2-action-tertiary": shouldHighlightOnHover,
              })}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  cell={cell}
                  columns={columns}
                  columnIndex={cell.column.getIndex()}
                  headerGroups={table.getHeaderGroups()[0]}
                  className={`sticky ${
                    (cell.column.columnDef as ExtendedColumnDef<unknown>)
                      ?.sticky
                      ? "z-3"
                      : "z-2"
                  }`}
                  style={{ top: tableHeadHeight }}
                  shouldHighlightOnHover={shouldHighlightOnHover}
                  currentColumnIndexHovered={props.currentColumnIndexHovered}
                  setCurrentColumnIndexHovered={
                    props.setCurrentColumnIndexHovered
                  }
                />
              ))}
            </tr>
          ))}
          {table.getCenterRows().map((row, rowIndex) => (
            <tr
              key={row.id}
              className={clsx("h-[1px]", {
                "hover:bg-v2-action-tertiary": shouldHighlightOnHover,
                "bg-v2-secondary": rowIndex % 2 === 0,
                "bg-v2-primary": rowIndex % 2 === 1,
              })}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  cell={cell}
                  columns={columns}
                  columnIndex={cell.column.getIndex()}
                  headerGroups={table.getHeaderGroups()[0]}
                  shouldHighlightOnHover={shouldHighlightOnHover}
                  currentColumnIndexHovered={props.currentColumnIndexHovered}
                  setCurrentColumnIndexHovered={
                    props.setCurrentColumnIndexHovered
                  }
                />
              ))}
            </tr>
          ))}
        </>
      )}
    </tbody>
  );
};

export const MemoizedTableBody = memo(
  TableBody,
  (prev, next) => prev.table.options.data === next.table.options.data,
) as typeof TableBody;
